import { useState, useEffect } from 'react';
import { StickyNote, Check, ExternalLink, Instagram, Facebook, Music2 } from 'lucide-react';
import { resolveSocialUrl } from '../../utils/socialLink';

const NETWORKS = [
  { value: 'instagram', Icon: Instagram, label: 'Instagram', activeBg: '#E4405F' },
  { value: 'facebook',  Icon: Facebook,  label: 'Facebook',  activeBg: '#1877F2' },
  { value: 'tiktok',    Icon: Music2,    label: 'TikTok',    activeBg: '#000000' },
];

/**
 * Compact inline editor for admin-only order metadata:
 *   - social_media_network : instagram | facebook | tiktok  (network picker)
 *   - social_media_account : customer handle for that network
 *   - admin_note           : free-form internal note
 */
export default function OrderMetaInline({ order, onSave }) {
  const [network, setNetwork] = useState(order.social_media_network || '');
  const [social, setSocial] = useState(order.social_media_account || '');
  const [note, setNote] = useState(order.admin_note || '');
  const [saving, setSaving] = useState(false);

  // Sync from parent
  useEffect(() => {
    setNetwork(order.social_media_network || '');
    setSocial(order.social_media_account || '');
    setNote(order.admin_note || '');
  }, [order.social_media_network, order.social_media_account, order.admin_note]);

  const dirty =
    social !== (order.social_media_account || '') ||
    note !== (order.admin_note || '') ||
    network !== (order.social_media_network || '');

  const submit = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await onSave({
        social_media_account: social,
        social_media_network: network,
        admin_note: note,
      });
    } finally {
      setSaving(false);
    }
  };

  const url = resolveSocialUrl(social, network);

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 mb-3 p-3 bg-[#F9F8F6] border border-[#E0DFDD]"
      data-testid={`order-meta-${order.order_id}`}
    >
      {/* Social row: network picker + handle input + open link */}
      <div className="flex items-center gap-1 border border-[#E0DFDD] bg-white pl-1 pr-2 py-1">
        {/* Network picker */}
        <div className="flex items-center gap-0.5" data-testid={`order-social-network-${order.order_id}`}>
          {NETWORKS.map(({ value, Icon, label, activeBg }) => (
            <button
              key={value}
              type="button"
              onClick={() => setNetwork(value === network ? '' : value)}
              className={`p-1.5 rounded transition-colors ${network === value ? 'text-white' : 'text-[#6B6A68] hover:text-[#1A1514]'}`}
              style={network === value ? { backgroundColor: activeBg } : {}}
              title={label}
              data-testid={`order-network-${value}-${order.order_id}`}
              aria-label={label}
              aria-pressed={network === value}
            >
              <Icon size={13} />
            </button>
          ))}
        </div>
        <div className="w-px h-4 bg-[#E0DFDD]" />
        <input
          type="text"
          value={social}
          onChange={(e) => setSocial(e.target.value)}
          placeholder={network ? `Nom de compte ${network}` : "Choisir réseau puis saisir le compte"}
          className="flex-1 text-xs focus:outline-none bg-transparent min-w-0 px-1"
          data-testid={`order-social-${order.order_id}`}
        />
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#6B6A68] hover:text-[#1A1514] flex-shrink-0"
            title={`Ouvrir ${url}`}
            data-testid={`order-social-open-${order.order_id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={12} />
          </a>
        )}
      </div>

      {/* Internal note */}
      <div className="flex items-center gap-2 border border-[#E0DFDD] bg-white px-2 py-1.5">
        <StickyNote size={12} className="text-[#6B6A68] flex-shrink-0" />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note interne"
          className="flex-1 text-xs focus:outline-none bg-transparent min-w-0"
          data-testid={`order-note-${order.order_id}`}
        />
      </div>

      {/* Save */}
      <button
        type="button"
        onClick={submit}
        disabled={!dirty || saving}
        className="px-3 py-1.5 text-xs font-medium bg-[#1A1514] text-white hover:bg-[#2a2524] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1"
        data-testid={`order-meta-save-${order.order_id}`}
      >
        <Check size={12} /> {saving ? '...' : 'Enregistrer'}
      </button>
    </div>
  );
}
